/* component-custom-scroller.js — <custom-scroller>
   A scroll container with a fully custom scrollbar (no native arrows).

   The native scrollbar is hidden in CSS; this component draws a thumb that
   reflects scroll position and can be dragged. The bar auto-hides when the
   content doesn't overflow.

   Markup:
     <custom-scroller>
       <div data-scroller-viewport> …scrollable content… </div>
       <div data-scroller-track><div data-scroller-thumb></div></div>
     </custom-scroller>
*/
if (!customElements.get("custom-scroller")) {
  customElements.define(
    "custom-scroller",
    class CustomScroller extends HTMLElement {
      constructor() {
        super();
        this._update = this._update.bind(this);
        this._onThumbDown = this._onThumbDown.bind(this);
        this._onMove = this._onMove.bind(this);
        this._onUp = this._onUp.bind(this);
      }

      connectedCallback() {
        this.viewport = this.querySelector("[data-scroller-viewport]");
        this.track = this.querySelector("[data-scroller-track]");
        this.thumb = this.querySelector("[data-scroller-thumb]");
        if (!this.viewport || !this.track || !this.thumb) return;

        this.viewport.addEventListener("scroll", this._update, { passive: true });
        this.thumb.addEventListener("pointerdown", this._onThumbDown);
        window.addEventListener("resize", this._update);

        // Recompute when items are added/removed or the box resizes.
        if (window.ResizeObserver) {
          this._ro = new ResizeObserver(this._update);
          this._ro.observe(this.viewport);
        }
        if (window.MutationObserver) {
          this._mo = new MutationObserver(this._update);
          this._mo.observe(this.viewport, { childList: true, subtree: true });
        }

        this._update();
      }

      disconnectedCallback() {
        this.viewport?.removeEventListener("scroll", this._update);
        this.thumb?.removeEventListener("pointerdown", this._onThumbDown);
        window.removeEventListener("resize", this._update);
        window.removeEventListener("pointermove", this._onMove);
        window.removeEventListener("pointerup", this._onUp);
        this._ro?.disconnect();
        this._mo?.disconnect();
      }

      _update() {
        const { scrollHeight, clientHeight, scrollTop } = this.viewport;
        const overflow = scrollHeight > clientHeight + 1;
        this.classList.toggle("is-scrollable", overflow);
        if (!overflow) return;

        const trackH = this.track.clientHeight;
        const thumbH = Math.max(28, (clientHeight / scrollHeight) * trackH);
        const maxScroll = scrollHeight - clientHeight;
        const maxThumb = trackH - thumbH;
        const top = maxScroll > 0 ? (scrollTop / maxScroll) * maxThumb : 0;

        this.thumb.style.height = thumbH + "px";
        this.thumb.style.transform = "translateY(" + top + "px)";
      }

      _onThumbDown(e) {
        e.preventDefault();
        this._startY = e.clientY;
        this._startScroll = this.viewport.scrollTop;
        this.classList.add("is-dragging");
        window.addEventListener("pointermove", this._onMove);
        window.addEventListener("pointerup", this._onUp);
      }

      _onMove(e) {
        const trackH = this.track.clientHeight;
        const thumbH = this.thumb.offsetHeight;
        const maxScroll = this.viewport.scrollHeight - this.viewport.clientHeight;
        const maxThumb = trackH - thumbH;
        if (maxThumb <= 0) return;
        const delta = e.clientY - this._startY;
        this.viewport.scrollTop = this._startScroll + (delta / maxThumb) * maxScroll;
      }

      _onUp() {
        this.classList.remove("is-dragging");
        window.removeEventListener("pointermove", this._onMove);
        window.removeEventListener("pointerup", this._onUp);
      }
    },
  );
}
